import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Alert,
  Tooltip
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Launch as LaunchIcon,
  Delete as DeleteIcon,
  NotificationImportant as AlertIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/Auth0Context';
import { handleNotificationClick, getNotificationActionDescription } from '../../utils/notificationNavigation';
import { useNotifications } from '../../context/NotificationContext';

const NotificationsPage = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sendingAlert, setSendingAlert] = useState(false);

  // Use the centralized notification context
  const { 
    notifications, 
    loading, 
    fetchNotifications, 
    markAsRead, 
    markAllAsRead 
  } = useNotifications();

  // Send manual WPS alert (Admin only)
  const sendManualWPSAlert = async () => {
    if (!isAdmin()) {
      console.error('Only administrators can send manual WPS alerts');
      return;
    }

    // Confirmation dialog
    const confirmed = window.confirm(
      '📢 Send WPS Update Alerts?\n\n' +
      'This will send countdown alerts to all users with overdue WPS entries.\n\n' +
      'Users will receive notifications reminding them to update actual hours.\n\n' +
      'Continue?'
    );

    if (!confirmed) {
      return;
    }

    try {
      setSendingAlert(true);
      console.log('📢 Sending manual WPS alerts...');

      const response = await axios.post('/api/alerts/trigger-wps', {
        alertType: 'daily'
      });

      console.log('✅ Manual WPS alerts sent successfully:', response.data);

      // Show success message
      alert(`✅ WPS Update Alerts Sent!\n\nAlerts have been sent to all users with overdue WPS entries.\n\nTimestamp: ${response.data.timestamp}`);

      // Refresh notifications to show the new alerts
      setTimeout(() => {
        fetchNotifications();
      }, 2000); // Wait 2 seconds for alerts to be processed

    } catch (error) {
      console.error('❌ Error sending manual WPS alerts:', error);
      alert('❌ Failed to send WPS alerts. Please try again.');
    } finally {
      setSendingAlert(false);
    }
  };

  // Delete notification (Admin only)
  const deleteNotification = async (notificationId) => {
    if (!isAdmin()) {
      console.error('Only administrators can delete notifications');
      return;
    }

    try {
      console.log('🗑️ Deleting notification:', notificationId);

      const response = await axios.delete(`/api/notifications/${notificationId}`);
      console.log('✅ Successfully deleted notification:', response.data);

      // Refresh notifications to update the list
      fetchNotifications();
    } catch (error) {
      console.error('❌ Error deleting notification:', error);
    }
  };

  // Get notification icon
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'wps_alert':
        return <WarningIcon color="warning" />;
      case 'admin_wps_alert':
        return <ErrorIcon color="error" />;
      case 'wps_daily_countdown':
        return <WarningIcon color="warning" />;
      case 'wps_monthly_summary':
        return <InfoIcon color="info" />;
      case 'admin_wps_monthly_summary':
        return <InfoIcon color="info" />;
      case 'technical_meeting_reminder':
        return <InfoIcon color="info" />;
      case 'wps_created':
        return <CheckCircleIcon color="success" />;
      case 'wps_actual_hours_updated':
        return <WarningIcon color="warning" />;
      case 'info':
        return <InfoIcon color="info" />;
      case 'success':
        return <CheckCircleIcon color="success" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  // Parse notification title and content from message
  const parseNotification = (notification) => {
    if (!notification.message) return { title: 'Notification', content: '' };
    
    // Check if message contains a title separator ': '
    const separatorIndex = notification.message.indexOf(': ');
    if (separatorIndex > 0 && separatorIndex < 100) { // Title should be reasonable length
      return {
        title: notification.message.substring(0, separatorIndex),
        content: notification.message.substring(separatorIndex + 2)
      };
    }
    
    // If no title separator, use first part of message as title
    const firstLine = notification.message.split('\n')[0];
    if (firstLine.length > 50) {
      return {
        title: firstLine.substring(0, 50) + '...',
        content: notification.message
      };
    }
    
    return {
      title: firstLine || 'Notification',
      content: notification.message
    };
  };

  // Format date
  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  // Filter notifications
  const filteredNotifications = notifications.filter(notification => {
    const { title } = parseNotification(notification);
    
    const matchesReadFilter = filter === 'all' || 
      (filter === 'unread' && !notification.read) ||
      (filter === 'read' && notification.read);
    
    const matchesTypeFilter = typeFilter === 'all' || notification.type === typeFilter;
    
    const matchesSearch = searchTerm === '' || 
      title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notification.message.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesReadFilter && matchesTypeFilter && matchesSearch;
  });

  // Load notifications when component mounts
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          {isAdmin() ? 'Notifications Management' : 'My Notifications'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {isAdmin() && (
            <Button
              variant="contained"
              color="warning"
              startIcon={<AlertIcon />}
              onClick={sendManualWPSAlert}
              disabled={sendingAlert}
              sx={{
                '&:hover': {
                  backgroundColor: '#f57c00',
                }
              }}
            >
              {sendingAlert ? 'Sending...' : 'Send WPS Alerts'}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<MarkEmailReadIcon />}
            onClick={markAllAsRead}
          >
            Mark All Read
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={fetchNotifications}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: '12px' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filter}
                label="Status"
                onChange={(e) => setFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="unread">Unread</MenuItem>
                <MenuItem value="read">Read</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                label="Type"
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="wps_daily_countdown">Daily WPS Countdown</MenuItem>
                {isAdmin() && <MenuItem value="admin_wps_critical">Admin WPS Critical</MenuItem>}
                <MenuItem value="wps_monthly_summary">WPS Monthly Summary</MenuItem>
                {isAdmin() && <MenuItem value="admin_wps_monthly_summary">Admin Monthly Summary</MenuItem>}
                <MenuItem value="technical_meeting_reminder">Technical Meeting Reminder</MenuItem>
                {isAdmin() && <MenuItem value="admin_technical_meeting_alert">Admin Meeting Alert</MenuItem>}
                {isAdmin() && <MenuItem value="wps_alert">WPS Alert (Legacy)</MenuItem>}
                {isAdmin() && <MenuItem value="admin_wps_alert">Admin WPS Alert (Legacy)</MenuItem>}
                {isAdmin() && <MenuItem value="wps_created">WPS Created</MenuItem>}
                {isAdmin() && <MenuItem value="wps_actual_hours_updated">WPS Actual Hours Updated</MenuItem>}
                <MenuItem value="info">Info</MenuItem>
                <MenuItem value="success">Success</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Search notifications"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by title or message..."
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Notifications Table */}
      <Paper sx={{ borderRadius: '12px', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell>Type</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Message</TableCell>
                {isAdmin() && <TableCell>User</TableCell>}
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={isAdmin() ? 7 : 6} align="center">
                    Loading notifications...
                  </TableCell>
                </TableRow>
              ) : filteredNotifications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin() ? 7 : 6} align="center">
                    No notifications found
                  </TableCell>
                </TableRow>
              ) : (
                filteredNotifications.map((notification) => {
                  const { title, content } = parseNotification(notification);
                  return (
                    <TableRow 
                      key={notification.id}
                      sx={{ 
                        bgcolor: notification.read ? 'rgba(76, 175, 80, 0.05)' : 'rgba(25, 118, 210, 0.08)',
                        borderLeft: notification.read ? '4px solid #4caf50' : '4px solid #1976d2',
                        '&:hover': { 
                          bgcolor: notification.read ? 'rgba(76, 175, 80, 0.1)' : 'rgba(25, 118, 210, 0.12)',
                          cursor: 'pointer'
                        }
                      }}
                      onClick={() => {
                        handleNotificationClick(notification, navigate, markAsRead, user);
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getNotificationIcon(notification.type)}
                          <Typography variant="caption">
                            {notification.type?.replace('_', ' ').toUpperCase()}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          fontWeight={notification.read ? 'normal' : 'bold'}
                          color={notification.read ? 'text.secondary' : 'text.primary'}
                        >
                          {title}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 300, maxWidth: 500 }}>
                        <Typography
                          variant="body2"
                          color={notification.read ? 'text.secondary' : 'text.primary'}
                          sx={{
                            wordWrap: 'break-word',
                            whiteSpace: 'normal',
                            lineHeight: 1.4
                          }}
                        >
                          {content}
                        </Typography>
                      </TableCell>
                      {isAdmin() && (
                        <TableCell>
                          <Typography variant="body2" color={notification.read ? 'text.secondary' : 'text.primary'}>
                            User ID: {notification.user_id}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        <Chip
                          label={notification.read ? 'Read' : 'Unread'}
                          color={notification.read ? 'success' : 'primary'}
                          size="small"
                          variant={notification.read ? 'outlined' : 'filled'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color={notification.read ? 'text.secondary' : 'text.primary'}>
                          {formatDate(notification.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title={getNotificationActionDescription(notification)}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent row click
                                handleNotificationClick(notification, navigate, markAsRead, user);
                              }}
                              color="primary"
                            >
                              <LaunchIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent row click
                              markAsRead(notification.id);
                            }}
                            title={notification.read ? 'Already read' : 'Mark as read'}
                            disabled={notification.read}
                            color={notification.read ? 'success' : 'primary'}
                          >
                            <MarkEmailReadIcon fontSize="small" />
                          </IconButton>
                          {isAdmin() && (
                            <Tooltip title="Delete notification">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent row click
                                  if (window.confirm('Are you sure you want to delete this notification?')) {
                                    deleteNotification(notification.id);
                                  }
                                }}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Summary */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredNotifications.length} of {notifications.length} notifications
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {notifications.filter(n => !n.read).length} unread notifications
        </Typography>
      </Box>
    </Box>
  );
};

export default NotificationsPage;
